<template>
  <main
    class="flex sm:flex-col-reverse sm:overflow-y-auto md:h-full md:max-h-full md:flex-row md:overflow-hidden"
  >
    <Details class="shrink-0 md:basis-[40%] lg:basis-[25vw]" />
    <div
      class="flex flex-col gap-6 px-24 pb-24 md:h-full md:grow"
      data-test="conversation-wrapper"
    >
      <div ref="scrollContainer" class="grow overflow-y-auto">
        <div
          v-if="conversationsPending || conversationsError || !conversations"
        >
          <div
            v-if="conversationsPending"
            class="flex w-full grow flex-col items-center p-16"
          >
            <ConversationSkeleton />
          </div>
          <ErrorInfoBlock
            v-else
            class="mt-24"
            @try-again="refreshConversations"
          />
        </div>

        <div v-else class="mb-6 space-y-24">
          <div
            v-for="conversation in conversations"
            :key="conversation.requestId"
          >
            <Conversation :conversation />
          </div>
        </div>
      </div>

      <ActionBar
        data-test="conversation-seller-action-bar"
        @message-sent="refreshConversations"
        @problem-reported="refreshConversations"
        @refund-created="refreshConversations"
      />
    </div>
  </main>
</template>

<script setup lang="ts">
import { useAsyncData } from '#imports'
import { nextTick, onMounted, ref, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'

import ErrorInfoBlock from '../../../../components/ErrorInfoBlock/ErrorInfoBlock.vue'
import { useOrderlinePublicIdFromRoute } from '../../composables/useOrderlinePublicIdFromRoute'

import ActionBar from './components/ActionBar/ActionBar.vue'
import Conversation from './components/Conversation/Conversation.vue'
import ConversationSkeleton from './components/Conversation/ConversationSkeleton.vue'
import Details from './components/Details/Details.vue'

const scrollContainer = ref<HTMLDivElement | null>(null)

const $httpFetch = useHttpFetch()

const orderlinePublicId = useOrderlinePublicIdFromRoute()

const {
  data: conversations,
  pending: conversationsPending,
  refresh: refreshConversations,
  error: conversationsError,
} = useAsyncData(
  `orderline-conversation-${orderlinePublicId.value}`,
  () =>
    $httpFetch(
      '/seller-after-sales/api/v1/orderlines/:orderlinePublicId/conversation',
      {
        pathParams: { orderlinePublicId: orderlinePublicId.value },
      },
    ),
  { watch: [orderlinePublicId] },
)

function scrollToLastMessage({ smoothly }: { smoothly?: boolean } = {}) {
  if (scrollContainer.value?.scrollTo) {
    scrollContainer.value.scrollTo({
      top: scrollContainer.value.scrollHeight,
      behavior: smoothly ? 'smooth' : 'instant',
    })
  }
}

onMounted(() => {
  scrollToLastMessage()
})

watch(conversationsPending, async (isPending) => {
  if (!isPending) {
    await nextTick()
    scrollToLastMessage()
  }
})
</script>
